import {
  TRACE_SIDECAR_SCHEMA_VERSION,
  validateTraceSidecar,
  type TraceSidecar,
} from "../../../data/schemas";
import {
  isRecord,
  validationFailure,
  validationSuccess,
  type ValidationResult,
} from "../../../data/validation";
import { isLinearControlMeasure, referentIdFor } from "../blank-classification";
import {
  type LabelledMark,
  type SidecarVerification,
  type StatedGrid,
  partitionEstimates,
  verifySidecar,
} from "../automatic-orders/sidecar-verification";

/**
 * Sidecars written before Ruling 25 carry no grid, referent or grid state.
 *
 * The migration adds none of them. An absent grid is not a missing value to
 * be filled in: it means the sidecar never claimed a grid for that marker, and
 * inventing one here would put a grid into the application that nobody
 * authored. Every v1 file therefore migrates to a v2 file with the trio absent
 * throughout, which resolves no blank and reports as no source.
 */
/**
 * `123 456` and `123456` are the same grid.
 *
 * A six-figure grid is normally written as two groups of three — it is how
 * doc 03 writes them and how they are spoken. The first authored sidecar
 * wrote all nineteen that way and the loader refused the file on its first
 * marker, which was the loader being narrower than the thing it models.
 *
 * The same principle as the referent vocabulary, ruled on 18 September 2026:
 * the value is the concept, not its spacing. One space between the groups is
 * accepted and stored as six digits, so everything downstream sees one shape.
 * Nothing else is accepted: a four-figure value, a zone designator or any
 * other separator is still refused, naming the marker.
 */
function normalizeGrids(
  value: Record<string, unknown>,
): Record<string, unknown> {
  const normalizePoint = (point: unknown): unknown => {
    if (!isRecord(point) || typeof point.grid !== "string") return point;
    const collapsed = point.grid.trim().replace(/^(\d{3})\s(\d{3})$/, "$1$2");
    return collapsed === point.grid ? point : { ...point, grid: collapsed };
  };
  const normalizeList = (list: unknown): unknown =>
    Array.isArray(list) ? list.map(normalizePoint) : list;
  return {
    ...value,
    features: normalizeList(value.features),
    markers: normalizeList(value.markers),
  };
}

export function migrateTraceSidecar(
  value: unknown,
): ValidationResult<TraceSidecar> {
  if (!isRecord(value)) {
    return validationFailure("Trace sidecar", "$", "expected a JSON object");
  }
  if (
    value.schemaVersion === TRACE_SIDECAR_SCHEMA_VERSION ||
    value.schemaVersion === 0 ||
    value.schemaVersion === 1 ||
    value.schemaVersion === 2 ||
    value.schemaVersion === 3 ||
    // Ruling 67, schema version 5. The migration from 4 adds nothing, on the
    // same reasoning as the version 2 migration above: `status`, `echelon`,
    // `frame` and `icon` say how the trace draws a mark, and a version 4
    // sidecar never claimed any of them. Supplying a default would put a
    // drawing into the application that nobody read off a trace, and Ruling
    // 67d says an undefined mark is drawn as today's plain marker — which is
    // exactly what all four absent produces.
    value.schemaVersion === 4
  ) {
    const migrated = normalizeGrids({
      ...value,
      schemaVersion: TRACE_SIDECAR_SCHEMA_VERSION,
    });
    const validated = validateTraceSidecar(migrated, "Trace sidecar");
    return validated.ok ? refuseGridsOnLines(validated.value) : validated;
  }
  return validationFailure(
    "Trace sidecar",
    "$.schemaVersion",
    `no migration registered for schemaVersion ${String(value.schemaVersion)}`,
  );
}

/**
 * Ruling 32, 18 September 2026. A report line, boundary, axis, route, lane,
 * LD or LOE is a line. It has no single grid, and one written on it is
 * refused, naming the measure.
 *
 * This is a doctrinal refusal rather than a shape check, which is why it sits
 * here and not in the schema: it needs the referent vocabulary to know which
 * measures are lines, and the vocabulary is the thing Ruling 29 made
 * authoritative.
 *
 * A located measure — a start point, a rendezvous, an area of interest, an
 * objective — carries a grid and is untouched. A term with no geometry stated
 * is untouched too; Ruling 32 named the measures it named.
 */
function refuseGridsOnLines(
  sidecar: TraceSidecar,
): ValidationResult<TraceSidecar> {
  for (const [key, points] of [
    ["features", sidecar.features],
    ["markers", sidecar.markers],
  ] as const) {
    for (const [index, point] of points.entries()) {
      if (point.grid === undefined || point.referent === undefined) continue;
      if (!isLinearControlMeasure(point.referent)) continue;
      return validationFailure(
        "Trace sidecar",
        `$.${key}[${String(index)}].grid`,
        `marker "${point.id}": ${point.referent} is a line and has no single grid`,
      );
    }
  }
  return validationSuccess(sidecar);
}

/**
 * Every mark the sidecar carries a grid for, as the protocol reads it.
 *
 * **A mark with no referent is included**, carrying an empty one. It used to
 * be dropped, on the reasoning that the ordering check works on referents and
 * a mark naming nothing cannot be out of a sequence it is not in — which is
 * true, and which the ordering check enforces for itself by looking only for
 * start and release points.
 *
 * Dropping it here was wrong for the other two checks. Ruling 41 makes the
 * enemy position the standing case of an estimate that rests, and an enemy
 * position is exactly a mark this sidecar gives no referent: an order set
 * describes the enemy, it does not grid him, so there is no referent term for
 * him either. Excluding him meant the one case the ruling names never reached
 * the partition.
 */
function protocolMarks(sidecar: TraceSidecar): readonly LabelledMark[] {
  return [...sidecar.features, ...sidecar.markers].flatMap((point) =>
    point.grid === undefined
      ? []
      : [
          {
            grid: point.grid,
            gridState: point.gridState ?? "",
            id: point.id,
            label: point.label,
            referent:
              point.referent === undefined
                ? ""
                : (referentIdFor(point.referent) ?? point.referent),
          },
        ],
  );
}

/** The three checks of Ruling 33, run over a loaded sidecar. */
export function verifyLoadedSidecar(
  sidecar: TraceSidecar,
  stated: readonly StatedGrid[],
): SidecarVerification {
  const marks = protocolMarks(sidecar);
  const { anchored, resting } = partitionEstimates(marks, stated);
  return verifySidecar(
    anchored,
    // The label goes through: Ruling 39 pairs a release point with its own
    // numbered start point, and the number is in the label.
    marks.map(({ grid, id, label, referent }) => ({
      grid,
      id,
      label,
      referent,
    })),
    [],
    resting,
  );
}

/**
 * Ruling 36, 18 September 2026. **A sidecar that fails check 1 is refused as
 * Source A**, at the loader, so a failed file cannot quietly become a source
 * the moment the `automaticOrders` flag turns on.
 *
 * The refusal needs the grids the order set states, which only the caller
 * has. Supply them and the check runs; omit them and the file loads
 * unverified, which is what every caller that is not resolving grids wants —
 * the trace pane does not need an order set to draw a marking.
 */
function refuseFailedAnchor(
  sidecar: TraceSidecar,
  stated: readonly StatedGrid[],
): ValidationResult<TraceSidecar> {
  const verification = verifyLoadedSidecar(sidecar, stated);
  if (verification.anchor.outcome !== "failed") {
    return validationSuccess(sidecar);
  }
  const worst = verification.anchor.disagreements.reduce((highest, entry) =>
    entry.separationM > highest.separationM ? entry : highest,
  );
  return validationFailure(
    "Trace sidecar",
    "$.markers",
    `the anchor check failed: ${String(verification.anchor.disagreements.length)} of ${String(verification.anchor.checked)} estimates disagree with the order set by more than the tolerance, the worst by ${String(Math.round(worst.separationM))} m on marker "${worst.id}"`,
  );
}

export interface SidecarLoadOptions {
  /** Grids the order set states, for Ruling 33's anchor check. */
  readonly stated?: readonly StatedGrid[];
}

export function parseTraceSidecarText(
  serialized: string,
  options: SidecarLoadOptions = {},
): ValidationResult<TraceSidecar> {
  let value: unknown;
  try {
    value = JSON.parse(serialized) as unknown;
  } catch {
    return validationFailure("Trace sidecar", "$", "invalid JSON");
  }
  const migrated = migrateTraceSidecar(value);
  if (!migrated.ok || options.stated === undefined) return migrated;
  return refuseFailedAnchor(migrated.value, options.stated);
}

/** The filename alone, lower-cased, with any directory dropped. */
function imageLeaf(value: string): string {
  return value.replaceAll("\\", "/").split("/").at(-1)?.toLowerCase() ?? "";
}

/**
 * Space, underscore and hyphen are one character, and runs collapse.
 *
 * Ruling 51 of 7 October 2026: "Trace_5_-_Practice_-_Overlay.jpg" and
 * "Trace 5 - Practice - Overlay.jpg" are one file. The separators differ
 * because one name came from a file system and the other from a document,
 * and refusing the overlay over that is refusing it over punctuation.
 */
function normalizeSeparators(leaf: string): string {
  return leaf.replaceAll(/[\s_-]+/g, " ").trim();
}

export type SidecarImageMatch = "exact" | "normalized" | "different";

/**
 * Whether the overlay names the image that is loaded, and how closely.
 *
 * `normalized` is a match, but the screen says so, because a trainee whose
 * two filenames differ should know they differ.
 */
export function matchSidecarImage(
  sidecarImage: string,
  selectedImage: string,
): SidecarImageMatch {
  const sidecarLeaf = imageLeaf(sidecarImage);
  const selectedLeaf = imageLeaf(selectedImage);
  if (sidecarLeaf === selectedLeaf) return "exact";
  if (normalizeSeparators(sidecarLeaf) === normalizeSeparators(selectedLeaf)) {
    return "normalized";
  }
  return "different";
}

export function sidecarMatchesImage(
  sidecarImage: string,
  selectedImage: string,
): boolean {
  return matchSidecarImage(sidecarImage, selectedImage) !== "different";
}

/**
 * The refusal, naming both strings.
 *
 * "Names a different image" told the trainee nothing: he could not see which
 * of the two names was wrong, or how. Ruling 51 requires both.
 */
export function describeImageMismatch(
  sidecarImage: string,
  selectedImage: string,
): string {
  return `The overlay expects the image "${imageLeaf(sidecarImage)}" and the image loaded is "${imageLeaf(selectedImage)}". Correct the overlay's image field, or load the image it names.`;
}
